import { formatCurrency, formatNumber } from "../lib/format";
import type { CanonicalScreenerSummary } from "../lib/types";

interface ScreenerSummaryProps {
  summary: CanonicalScreenerSummary;
}

function InlineMetric({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex min-w-0 items-baseline gap-1.5 px-2 py-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
      {detail ? <span className="truncate text-[10px] text-muted-foreground">{detail}</span> : null}
    </div>
  );
}

export function ScreenerSummary({ summary }: ScreenerSummaryProps) {
  return (
    <section
      aria-label="Screener summary"
      className="flex min-h-9 flex-wrap items-center divide-x divide-border border-b border-border bg-surface-inset text-xs"
    >
      <InlineMetric label="Rows" value={formatNumber(summary.totalRows)} />
      <InlineMetric
        label="Informative buys"
        value={formatCurrency(summary.informativeBuyNotional)}
        detail={`${formatNumber(summary.informativeBuyCount)} informative ${summary.informativeBuyCount === 1 ? "buy" : "buys"}`}
      />
      <InlineMetric
        label="Informative sells"
        value={formatCurrency(summary.informativeSellNotional)}
        detail={`${formatNumber(summary.informativeSellCount)} informative ${summary.informativeSellCount === 1 ? "sell" : "sells"}`}
      />
      <InlineMetric
        label="Priced coverage"
        value={`${summary.pricedCoveragePercent.toFixed(1)}%`}
        detail={`${formatNumber(summary.pricedRowCount)} priced rows`}
      />
      <InlineMetric
        label="Median score"
        value={summary.medianScore === null ? "N/A" : formatNumber(summary.medianScore)}
      />
    </section>
  );
}
